<template>
  <div class="animated fadeIn">
    <b-row>
      <!-- Summary Cards -->
      <b-col sm="6" lg="3">
        <b-card class="text-white bg-gradient-primary border-0">
          <b-card-body class="pb-0">
            <div class="text-value-lg">Rp 230.00 M</div>
            <div>Total Saldo</div>
          </b-card-body>
          <div class="chart-wrapper mx-3" style="height:70px;">
            <canvas :id="'chart-1'" height="70"></canvas>
          </div>
        </b-card>
      </b-col>

      <b-col sm="6" lg="3">
        <b-card class="text-white bg-gradient-info border-0">
          <b-card-body class="pb-0">
            <div class="text-value-lg">Rp 160.00 M</div>
            <div>Cash In</div>
          </b-card-body>
          <div class="chart-wrapper mx-3" style="height:70px;">
            <canvas :id="'chart-2'" height="70"></canvas>
          </div>
        </b-card>
      </b-col>

      <b-col sm="6" lg="3">
        <b-card class="text-white bg-gradient-warning border-0">
          <b-card-body class="pb-0">
            <div class="text-value-lg">Rp 115.00 M</div>
            <div>Cash Out</div>
          </b-card-body>
          <div class="chart-wrapper mx-3" style="height:70px;">
            <canvas :id="'chart-3'" height="70"></canvas>
          </div>
        </b-card>
      </b-col>

      <b-col sm="6" lg="3">
        <b-card class="text-white bg-gradient-danger border-0">
          <b-card-body class="pb-0">
            <div class="text-value-lg">Rp 45.00 M</div>
            <div>Net Cashflow</div>
          </b-card-body>
          <div class="chart-wrapper mx-3" style="height:70px;">
            <canvas :id="'chart-4'" height="70"></canvas>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Project Selector -->
    <b-row class="mb-3">
      <b-col>
        <b-card>
          <b-card-body class="p-3">
            <div class="d-flex justify-content-between align-items-center flex-wrap">
              <div class="mb-2 mb-md-0">
                <h6 class="mb-0">Pilih Project:</h6>
              </div>
              <div>
                <b-button-group size="sm">
                  <b-button 
                    variant="primary" 
                    :pressed="selectedProject === null"
                    @click="selectProject(null)"
                  >
                    Semua Project
                  </b-button>
                  <b-button 
                    v-for="project in projects" 
                    :key="project.id"
                    :variant="project.color"
                    :pressed="selectedProject === project.id"
                    @click="selectProject(project.id)"
                  >
                    {{ project.name }}
                  </b-button>
                </b-button-group>
              </div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Main Chart -->
    <b-row>
      <b-col>
        <b-card>
          <b-card-body>
            <b-row>
              <b-col sm="5">
                <h4 class="card-title mb-0">Tren Cashflow {{ selectedProjectName }}</h4>
                <div class="small text-muted">{{ currentMonth }}</div>
              </b-col>
              <b-col sm="7" class="d-none d-md-block">
                <b-button-group size="sm" class="float-right">
                  <b-button 
                    variant="outline-secondary"
                    :pressed="cashflowPeriod === '7d'"
                    @click="setCashflowPeriod('7d')"
                  >
                    7 Hari
                  </b-button>
                  <b-button 
                    variant="outline-secondary"
                    :pressed="cashflowPeriod === '30d'"
                    @click="setCashflowPeriod('30d')"
                  >
                    30 Hari
                  </b-button>
                  <b-button 
                    variant="outline-secondary"
                    :pressed="cashflowPeriod === '90d'"
                    @click="setCashflowPeriod('90d')"
                  >
                    90 Hari
                  </b-button>
                  <b-button 
                    variant="outline-secondary"
                    :pressed="cashflowPeriod === '1y'"
                    @click="setCashflowPeriod('1y')"
                  >
                    1 Tahun
                  </b-button>
                </b-button-group>
              </b-col>
            </b-row>
            <div class="chart-wrapper" style="height:300px;margin-top:40px;">
              <canvas ref="mainChart" height="300"></canvas>
            </div>
          </b-card-body>
          <b-card-footer>
            <b-row class="text-center">
              <b-col md sm="12" class="mb-sm-2 mb-0">
                <div class="text-muted">Cash In</div>
                <strong>{{ formatCurrency(currentStats.cashIn) }} ({{ calculatePercentage(currentStats.cashIn, currentStats.totalSaldo) }}%)</strong>
                <b-progress
                  class="progress-xs mt-2"
                  :value="calculatePercentage(currentStats.cashIn, currentStats.totalSaldo)"
                  variant="success"
                  :precision="1"
                />
              </b-col>
              <b-col md sm="12" class="mb-sm-2 mb-0 d-md-down-none">
                <div class="text-muted">Cash Out</div>
                <strong>{{ formatCurrency(currentStats.cashOut) }} ({{ calculatePercentage(currentStats.cashOut, currentStats.totalSaldo) }}%)</strong>
                <b-progress
                  class="progress-xs mt-2"
                  :value="calculatePercentage(currentStats.cashOut, currentStats.totalSaldo)"
                  variant="danger"
                  :precision="1"
                />
              </b-col>
              <b-col md sm="12" class="mb-sm-2 mb-0">
                <div class="text-muted">Net Cashflow</div>
                <strong>{{ formatCurrency(currentStats.netCashflow) }} ({{ calculatePercentage(currentStats.netCashflow, currentStats.cashIn) }}%)</strong>
                <b-progress
                  class="progress-xs mt-2"
                  :value="calculatePercentage(currentStats.netCashflow, currentStats.cashIn)"
                  variant="info"
                  :precision="1"
                />
              </b-col>
              <b-col md sm="12" class="mb-sm-2 mb-0">
                <div class="text-muted">Total Saldo</div>
                <strong>{{ formatCurrency(currentStats.totalSaldo) }}</strong>
                <b-progress
                  class="progress-xs mt-2"
                  :value="100"
                  variant="primary"
                  :precision="1"
                />
              </b-col>
            </b-row>
          </b-card-footer>
        </b-card>
      </b-col>
    </b-row>

    <!-- Bank Cards & Distribution Chart -->
    <b-row>
      <b-col lg="8">
        <!-- Bank Accounts -->
        <b-card header-tag="header">
          <template #header>
            <i class="fa fa-bank"></i> Saldo Per Bank {{ selectedProjectName }}
          </template>
          <b-card-body>
            <b-row>
              <b-col sm="6" v-for="bank in currentBanks" :key="bank.id" class="mb-3">
                <div class="border-left p-3 h-100" :class="`border-left-${bank.type === 'bca' ? 'primary' : 'danger'}`">
                  <div class="d-flex align-items-center">
                    <div class="mr-3">
                      <div class="text-value-xl">{{ bank.logo }}</div>
                    </div>
                    <div class="flex-grow-1">
                      <div class="text-muted small">{{ bank.name }}</div>
                      <div class="text-value-lg">{{ formatCurrency(bank.balance) }}</div>
                      <div class="small text-muted">{{ bank.account }}</div>
                      <b-progress
                        class="progress-xs mt-2"
                        :value="calculatePercentage(bank.balance, currentStats.totalSaldo)"
                        :variant="bank.type === 'bca' ? 'primary' : 'danger'"
                        :precision="1"
                      />
                    </div>
                  </div>
                  <div class="mt-3">
                    <b-row class="text-center">
                      <b-col>
                        <div class="small text-muted">Penempatan</div>
                        <div class="font-weight-bold text-success">{{ formatCurrency(bank.penempatan) }}</div>
                      </b-col>
                      <b-col>
                        <div class="small text-muted">Penarikan</div>
                        <div class="font-weight-bold text-danger">{{ formatCurrency(bank.penarikan) }}</div>
                      </b-col>
                    </b-row>
                  </div>
                </div>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>

      <b-col lg="4">
        <!-- Distribution Chart -->
        <b-card header-tag="header">
          <template #header>
            <i class="fa fa-pie-chart"></i> {{ selectedProject ? 'Distribusi Saldo' : 'Distribusi Per Project' }}
          </template>
          <b-card-body>
            <div class="chart-wrapper" style="height: 280px;">
              <canvas ref="distributionChart" height="280"></canvas>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Projects Grid -->
    <b-row v-if="selectedProject === null">
      <b-col lg="3" md="6" v-for="project in projects" :key="project.id">
        <b-card 
          class="cursor-pointer"
          :class="{ 'border-primary': selectedProject === project.id }"
          @click.native="selectProject(project.id)"
        >
          <b-card-body>
            <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <i :class="[getProjectIcon(project.icon), 'fa-2x', `text-${project.color}`]"></i>
                </div>
                <div>
                    <b-badge :variant="getProjectStatusColor(project.status)">
                    {{ project.status }}
                    </b-badge>
                </div>
            </div>
            <h5>{{ project.name }}</h5>
            <div class="progress-group mt-3">
              <div class="progress-group-header">
                <div class="text-muted small">Budget</div>
                <div class="font-weight-bold">{{ formatCurrency(project.budget) }}</div>
              </div>
              <div class="progress-group-header">
                <div class="text-muted small">Terpakai</div>
                <div class="font-weight-bold">{{ formatCurrency(project.spent) }}</div>
              </div>
              <b-progress
                class="progress-xs mt-2"
                :value="calculatePercentage(project.spent, project.budget)"
                :variant="project.color"
                :precision="1"
              />
              <div class="small text-muted">{{ calculatePercentage(project.spent, project.budget) }}% dari budget</div>
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Transactions Table -->
    <b-row>
      <b-col>
        <b-card header-tag="header">
          <template #header>
            <i class="fa fa-list"></i> Transaksi Terkini {{ selectedProjectName }}
            <div class="card-header-actions">
              <b-button variant="link" size="sm" class="card-header-action" @click="loadMoreTransactions">
                <i class="fa fa-refresh"></i>
              </b-button>
            </div>
          </template>
          <b-card-body>
            <b-table
              :items="currentTransactions"
              :fields="transactionFields"
              hover
              striped
              :per-page="10"
              :current-page="currentPage"
              responsive
            >
              <template #cell(date)="data">
                <div>{{ formatDate(data.value) }}</div>
              </template>

              <template #cell(project)="data">
                <b-badge variant="secondary">{{ data.value }}</b-badge>
              </template>

              <template #cell(bank)="data">
                <b-badge :variant="data.value === 'BCA' ? 'primary' : 'danger'">
                  {{ data.value }}
                </b-badge>
              </template>

              <template #cell(type)="data">
                <b-badge :variant="data.value === 'in' ? 'success' : 'danger'">
                  <i :class="data.value === 'in' ? 'fa fa-arrow-down' : 'fa fa-arrow-up'"></i>
                  {{ data.value === 'in' ? 'Masuk' : 'Keluar' }}
                </b-badge>
              </template>

              <template #cell(amount)="data">
                <span :class="data.item.type === 'in' ? 'text-success' : 'text-danger'" class="font-weight-bold">
                  {{ data.item.type === 'in' ? '+' : '-' }}{{ formatCurrency(data.value) }}
                </span>
              </template>

              <template #cell(balance)="data">
                <span class="font-weight-bold">{{ formatCurrency(data.value) }}</span>
              </template>
            </b-table>
            
            <b-pagination
              v-model="currentPage"
              :total-rows="currentTransactions.length"
              :per-page="10"
              align="center"
              size="sm"
            />
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Chart from 'chart.js'
import Dashboard from '../../apis/Dashboard';
const { financeCollection } = Dashboard;

export default {
  name: 'DashboardFinance',
  data() {
    return {
      currentMonth: '',
      cashflowPeriod: '30d',
      selectedProject: null,
      currentPage: 1,
      
      mainChart: null,
      distributionChart: null,
      miniCharts: [],
      
      labelsChart:[],
      datasetsChart:[],
      transactionFields: [
        { key: 'date', label: 'Tanggal', tdClass: 'text-center' },
        { key: 'project', label: 'Project' },
        { key: 'description', label: 'Deskripsi' },
        { key: 'bank', label: 'Bank', tdClass: 'text-center' },
        { key: 'type', label: 'Tipe', tdClass: 'text-center' },
        { key: 'amount', label: 'Jumlah', tdClass: 'text-right' },
        { key: 'balance', label: 'Saldo', tdClass: 'text-right' }
      ],
      
      projects: [
        /* {
          id: 1,
          name: 'Gopay Later',
          icon: 'phone',
          color: 'primary',
          status: 'active',
          budget: 100000000000,
          spent: 75000000000,
          banks: [
            {
              id: 1,
              name: 'Bank Central Asia (BCA)',
              logo: 'BCA',
              type: 'bca',
              account: '5260695888',
              balance: 30000000000,
              penempatan: 30000000000,
              penarikan: 0
            },
            {
              id: 2,
              name: 'Bank CIMB Niaga',
              logo: 'NIAGA',
              type: 'niaga',
              account: '706729544900',
              balance: 45000000000,
              penempatan: 50000000000,
              penarikan: 5000000000
            }
          ],
          cashIn: 80000000000,
          cashOut: 60000000000
        },
        {
          id: 2,
          name: 'Modal Toko',
          icon: 'home',
          color: 'info',
          status: 'active',
          budget: 60000000000,
          spent: 45000000000,
          banks: [
            {
              id: 1,
              name: 'Bank Central Asia (BCA)',
              logo: 'BCA',
              type: 'bca',
              account: '5260695888',
              balance: 20000000000,
              penempatan: 25000000000,
              penarikan: 5000000000
            },
            {
              id: 2,
              name: 'Bank CIMB Niaga',
              logo: 'NIAGA',
              type: 'niaga',
              account: '706729544900',
              balance: 25000000000,
              penempatan: 30000000000,
              penarikan: 5000000000
            }
          ],
          cashIn: 40000000000,
          cashOut: 30000000000
        },
        {
          id: 3,
          name: 'Gopay Pinjam',
          icon: 'dollar',
          color: 'warning',
          status: 'active',
          budget: 90000000000,
          spent: 65000000000,
          banks: [
            {
              id: 1,
              name: 'Bank Central Asia (BCA)',
              logo: 'BCA',
              type: 'bca',
              account: '5260695888',
              balance: 35000000000,
              penempatan: 40000000000,
              penarikan: 5000000000
            },
            {
              id: 2,
              name: 'Bank CIMB Niaga',
              logo: 'NIAGA',
              type: 'niaga',
              account: '706729544900',
              balance: 30000000000,
              penempatan: 35000000000,
              penarikan: 5000000000
            }
          ],
          cashIn: 50000000000,
          cashOut: 35000000000
        },
        {
          id: 4,
          name: 'Investment',
          icon: 'line-chart',
          color: 'success',
          status: 'pending',
          budget: 150000000000,
          spent: 80000000000,
          banks: [
            {
              id: 1,
              name: 'Bank Central Asia (BCA)',
              logo: 'BCA',
              type: 'bca',
              account: '5260695888',
              balance: 50000000000,
              penempatan: 60000000000,
              penarikan: 10000000000
            },
            {
              id: 2,
              name: 'Bank CIMB Niaga',
              logo: 'NIAGA',
              type: 'niaga',
              account: '706729544900',
              balance: 80000000000,
              penempatan: 90000000000,
              penarikan: 10000000000
            }
          ],
          cashIn: 70000000000,
          cashOut: 50000000000
        } */
      ],
      
      allTransactions: [
        {
          date: '2024-11-28',
          project: 'Gopay Later',
          description: 'Pengembalian APS to ABC',
          bank: 'BCA',
          type: 'in',
          amount: 100000000000,
          balance: 230000000000,
          projectId: 1
        },
        {
          date: '2024-11-25',
          project: 'Modal Toko',
          description: 'Pencairan Dana Toko',
          bank: 'NIAGA',
          type: 'out',
          amount: 25000000000,
          balance: 130000000000,
          projectId: 2
        },
        {
          date: '2024-11-22',
          project: 'Gopay Pinjam',
          description: 'Penerimaan Pembayaran',
          bank: 'BCA',
          type: 'in',
          amount: 45000000000,
          balance: 155000000000,
          projectId: 3
        },
        {
          date: '2024-11-20',
          project: 'Investment',
          description: 'Investasi Obligasi',
          bank: 'NIAGA',
          type: 'out',
          amount: 60000000000,
          balance: 110000000000,
          projectId: 4
        },
        {
          date: '2024-11-18',
          project: 'Gopay Later',
          description: 'Penempatan Dana',
          bank: 'BCA',
          type: 'out',
          amount: 30000000000,
          balance: 170000000000,
          projectId: 1
        },
        {
          date: '2024-11-15',
          project: 'Modal Toko',
          description: 'Penerimaan Revenue',
          bank: 'NIAGA',
          type: 'in',
          amount: 35000000000,
          balance: 200000000000,
          projectId: 2
        },
        {
          date: '2024-11-12',
          project: 'Gopay Pinjam',
          description: 'Transfer Operasional',
          bank: 'BCA',
          type: 'out',
          amount: 20000000000,
          balance: 165000000000,
          projectId: 3
        },
        {
          date: '2024-11-10',
          project: 'Investment',
          description: 'Return Investasi',
          bank: 'NIAGA',
          type: 'in',
          amount: 55000000000,
          balance: 185000000000,
          projectId: 4
        }
      ]
    }
  },
  
  computed: {
    selectedProjectName() {
      if (this.selectedProject === null) return ''
      const project = this.projects.find(p => p.id === this.selectedProject)
      return project ? `(${project.name})` : ''
    },
    
    currentStats() {
      if (this.selectedProject === null) {
        return {
          totalSaldo: this.projects.reduce((sum, p) => 
            sum + p.banks.reduce((s, b) => s + b.balance, 0), 0),
          cashIn: this.projects.reduce((sum, p) => sum + p.cashIn, 0),
          cashOut: this.projects.reduce((sum, p) => sum + p.cashOut, 0),
          netCashflow: this.projects.reduce((sum, p) => 
            sum + (p.cashIn - p.cashOut), 0)
        }
      } else {
        const project = this.projects.find(p => p.id === this.selectedProject)
        return {
          totalSaldo: project.banks.reduce((sum, b) => sum + b.balance, 0),
          cashIn: project.cashIn,
          cashOut: project.cashOut,
          netCashflow: project.cashIn - project.cashOut
        }
      }
    },
    
    currentBanks() {
      if (this.selectedProject === null) {
        const bankMap = {}
        this.projects.forEach(project => {
          project.banks.forEach(bank => {
            if (!bankMap[bank.account]) {
              bankMap[bank.account] = {
                ...bank,
                balance: 0,
                penempatan: 0,
                penarikan: 0
              }
            }
            bankMap[bank.account].balance += bank.balance
            bankMap[bank.account].penempatan += bank.penempatan
            bankMap[bank.account].penarikan += bank.penarikan
          })
        })
        return Object.values(bankMap)
      } else {
        const project = this.projects.find(p => p.id === this.selectedProject)
        return project ? project.banks : []
      }
    },
    
    currentTransactions() {
      if (this.selectedProject === null) {
        return this.allTransactions
      } else {
        return this.allTransactions.filter(tx => tx.projectId === this.selectedProject)
      }
    }
  },
  
  mounted() {
    this.updateDateTime()
    this.$nextTick(() => {
      this.initMiniCharts()
      this.initCharts()
    })
  },
  
  beforeDestroy() {
    if (this.mainChart) this.mainChart.destroy()
    if (this.distributionChart) this.distributionChart.destroy()
    this.miniCharts.forEach(chart => {
      if (chart) chart.destroy()
    })
  },
  async created(){
    try {
      
      this.getData();

    } catch (error) {
      
    }
  },
  watch: {
    selectedProject() {
      this.updateAllCharts()
    }
  },
  
  methods: {
    async getData(){
      try {
        this.labelsChart = [];
        let obj = {
          cashflowPeriod:this.cashflowPeriod
        }
        const {project:projectdatas,paymentColl} = await financeCollection.overview(obj);
        this.projects = projectdatas.map(project=>{
          return {
            id: project.pjxcode,
            name: project.pjxdesc,
            icon: 'phone',
            color: 'primary',
            status: 'active',
            budget: 100000000000,
            spent: 75000000000,banks:[]}
        })
        let objPayment ={
            label: 'Cash In',
            backgroundColor: 'rgba(23, 162, 184, 0.2)',
            borderColor: 'rgba(23, 162, 184, 1)',
            pointHoverBackgroundColor: '#fff',
            borderWidth: 2,
            data:[10, 14, 6, 20, 18, 24, 16, 20]
        };
        
        if(this.cashflowPeriod=='7d'){
          objPayment['data'] = [20, 20, 20, 20, 20, 20, 20]
        }

        if(this.cashflowPeriod=='90d'){
          objPayment['data'] = [160, 170, 165, 180, 175]
        }

        if(this.cashflowPeriod=='1y'){
          objPayment['data'] = [20, 20, 20, 20, 10, 2, 10, 20, 20, 20, 20, 20]
        }

        this.datasetsChart.push(objPayment)


      } catch (error) {
        console.log('error',error)
      }
    },
    updateDateTime() {
      const now = new Date()
      this.currentMonth = now.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })
    },
    
    selectProject(projectId) {
      this.selectedProject = projectId
    },
    
    getProjectStatusColor(status) {
      const colors = {
        'active': 'success',
        'pending': 'warning',
        'completed': 'info'
      }
      return colors[status] || 'secondary'
    },
    
    getProjectIcon(icon) {
      return 'fa fa-' + icon
    },
    
    formatCurrency(value) {
      if (!value && value !== 0) return '-'
      
      if (Math.abs(value) >= 1000000000) {
        return 'Rp ' + (value / 1000000000).toFixed(2) + ' M'
      } else if (Math.abs(value) >= 1000000) {
        return 'Rp ' + (value / 1000000).toFixed(2) + ' Jt'
      }
      
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0
      }).format(value)
    },
    
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('id-ID', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    },
    
    calculatePercentage(value, total) {
      if (!total) return 0
      return Math.round((value / total) * 100)
    },
    
    setCashflowPeriod(period) {
      this.cashflowPeriod = period
      this.updateMainChart()
      
    },
    
    initMiniCharts() {
      const datasets = [
        [65, 59, 84, 84, 51, 55, 40],
        [1, 18, 9, 17, 34, 22, 11],
        [78, 81, 80, 45, 34, 12, 40],
        [35, 23, 56, 22, 97, 23, 64]
      ]
      
      for (let i = 1; i <= 4; i++) {
        const canvas = document.getElementById('chart-' + i)
        if (canvas) {
          const ctx = canvas.getContext('2d')
          this.miniCharts.push(new Chart(ctx, {
            type: 'line',
            data: {
              labels: ['', '', '', '', '', '', ''],
              datasets: [{
                backgroundColor: 'transparent',
                borderColor: 'rgba(255,255,255,.55)',
                data: datasets[i-1],
                borderWidth: 2
              }]
            },
            options: {
              maintainAspectRatio: false,
              legend: {
                display: false
              },
              scales: {
                xAxes: [{
                  display: false
                }],
                yAxes: [{
                  display: false
                }]
              },
              elements: {
                point: {
                  radius: 0,
                  hitRadius: 10,
                  hoverRadius: 4,
                  hoverBorderWidth: 3
                }
              }
            }
          }))
        }
      }
    },
    
    initCharts() {
      this.initMainChart()
      this.initDistributionChart()
    },
    
    initMainChart() {
      const ctx = this.$refs.mainChart.getContext('2d')
      
      this.mainChart = new Chart(ctx, {
        type: 'line',
        data: this.getMainChartData(),
        options: {
          maintainAspectRatio: false,
          legend: {
            display: false
          },
          tooltips: {
            intersect: false,
            mode: 'index',
            position: 'nearest',
            callbacks: {
              label: function(tooltipItem, data) {
                return data.datasets[tooltipItem.datasetIndex].label + ': Rp ' + tooltipItem.yLabel + ' M'
              }
            }
          },
          responsive: true,
          scales: {
            xAxes: [{
              gridLines: {
                drawOnChartArea: false
              }
            }],
            yAxes: [{
              ticks: {
                beginAtZero: true,
                maxTicksLimit: 5,
                callback: function(value) {
                  return 'Rp ' + value + 'M'
                }
              }
            }]
          },
          elements: {
            line: {
              borderWidth: 2
            },
            point: {
              radius: 0,
              hitRadius: 10,
              hoverRadius: 4,
              hoverBorderWidth: 3
            }
          }
        }
      })
    },
    
    async getMainChartData() {
      try {
        let labels, data1, data2, data3,datasets=[];
        switch(this.cashflowPeriod) {
          case '7d':
            labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
            data1 = [65, 59, 80, 81, 56, 55, 40]
            data2 = [45, 39, 60, 61, 36, 35, 20]
            data3 = [20, 20, 20, 20, 20, 20, 20]
            break
          case '90d':
            labels = ['Agt', 'Sep', 'Okt', 'Nov', 'Des']
            data1 = [200, 220, 210, 240, 225]
            data2 = [160, 170, 165, 180, 175]
            data3 = [40, 50, 45, 60, 50]
            break
          case '1y':
            labels = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agt', 'Sep', 'Okt', 'Nov', 'Des']
            data1 = [78, 81, 80, 65, 34, 12, 40, 85, 65, 80, 82, 90]
            data2 = [58, 61, 60, 45, 24, 10, 30, 65, 45, 60, 62, 70]
            data3 = [20, 20, 20, 20, 10, 2, 10, 20, 20, 20, 20, 20]
            break
          default:
            labels = ['5 Nov', '10 Nov', '15 Nov', '20 Nov', '25 Nov', '30 Nov', '5 Des', '10 Des']
            data1 = [45, 52, 48, 65, 58, 72, 68, 75]
            data2 = [35, 38, 42, 45, 40, 48, 52, 55]
            data3 = [10, 14, 6, 20, 18, 24, 16, 20]
        }

        let obj = {
          cashflowPeriod:this.cashflowPeriod
        }
        const {paymentColl} = await financeCollection.overview(obj);

        let objPayment ={
            label: 'Cash In',
            backgroundColor: 'rgba(23, 162, 184, 0.2)',
            borderColor: 'rgba(23, 162, 184, 1)',
            pointHoverBackgroundColor: '#fff',
            borderWidth: 2,
            data:[10, 14, 6, 20, 18, 24, 16, 20]
        };
        
        if(this.cashflowPeriod=='7d'){
          objPayment['data'] = [20, 20, 20, 20, 20, 20, 20]
        }

        if(this.cashflowPeriod=='90d'){
          objPayment['data'] = [160, 170, 165, 180, 175]
        }

        if(this.cashflowPeriod=='1y'){
          objPayment['data'] = [20, 20, 20, 20, 10, 2, 10, 20, 20, 20, 20, 20]
        }
      
        datasets.push(objPayment);
        return {
          labels: labels,
          datasets
        }
      } catch (error) {
        console.log('Error on function getMainChartData',error)
      }
    },
    
    updateMainChart() {
      try {
        if (this.mainChart) {
          this.mainChart.data = this.getMainChartData()
          this.mainChart.update()
        }
      } catch (error) {
        console.log('error',error)
      }
      
    },
    
    initDistributionChart() {
      const ctx = this.$refs.distributionChart.getContext('2d')
      
      this.distributionChart = new Chart(ctx, {
        type: 'doughnut',
        data: this.getDistributionData(),
        options: {
          maintainAspectRatio: false,
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              fontSize: 11,
              padding: 15
            }
          }
        }
      })
    },
    
    getDistributionData() {
      if (this.selectedProject === null) {
        return {
          labels: this.projects.map(p => p.name),
          datasets: [{
            data: this.projects.map(p => 
              p.banks.reduce((sum, b) => sum + b.balance, 0) / 1000000000
            ),
            backgroundColor: [
              '#20a8d8',
              '#63c2de',
              '#ffc107',
              '#4dbd74'
            ],
            hoverBackgroundColor: [
              '#1985ac',
              '#4f9db8',
              '#d39e00',
              '#3a9d5d'
            ]
          }]
        }
      } else {
        return {
          labels: this.currentBanks.map(b => b.name),
          datasets: [{
            data: this.currentBanks.map(b => b.balance / 1000000000),
            backgroundColor: [
              '#20a8d8',
              '#f86c6b'
            ],
            hoverBackgroundColor: [
              '#1985ac',
              '#f63c3a'
            ]
          }]
        }
      }
    },
    
    updateAllCharts() {
      this.updateMainChart()
      if (this.distributionChart) {
        this.distributionChart.data = this.getDistributionData()
        this.distributionChart.update()
      }
    },
    
    loadMoreTransactions() {
      this.$bvToast.toast('Memuat transaksi lainnya...', {
        title: 'Loading',
        variant: 'info',
        solid: true
      })
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.border-left {
  border-left: 3px solid;
}

.border-left-primary {
  border-left-color: #20a8d8;
}

.border-left-danger {
  border-left-color: #f86c6b;
}

.text-value-xl {
  font-size: 2rem;
  font-weight: 700;
  color: #23282c;
}

.text-value-lg {
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
}

.progress-group {
  margin-bottom: 1rem;
}

.progress-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
}

.chart-wrapper {
  position: relative;
}

.progress-xs {
  height: 4px;
}

.bg-gradient-primary {
  background: linear-gradient(45deg, #20a8d8, #63c2de) !important;
}

.bg-gradient-info {
  background: linear-gradient(45deg, #63c2de, #a4d4e6) !important;
}

.bg-gradient-warning {
  background: linear-gradient(45deg, #ffc107, #ffd454) !important;
}

.bg-gradient-danger {
  background: linear-gradient(45deg, #f86c6b, #ff9d9c) !important;
}

.card-header-actions {
  float: right;
  margin-top: -0.5rem;
}

.card-header-action {
  padding: 0;
  margin-left: 0.5rem;
  color: inherit;
}

.animated {
  animation-duration: 0.3s;
  animation-fill-mode: both;
}

.fadeIn {
  animation-name: fadeIn;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>